/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Asset, Ducks, MenuUiArea } from '@wit/vtv-x-foundation-lib';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PrivateRoutePath } from 'routes/paths';
import Constants from 'configs/Constants';
import { StoreState } from 'store/store';
import * as MyTV from 'store/ducks/myTv';
import useIsOnViewportObserver from 'hooks/useIsOnViewportObserver';
import { SimpleHeader, Spinner } from 'ui/components';
import Overlay from 'ui/components/Hero/Overlay';
import BackgroundOverlay from 'assets/images/background/LandingPage_BackgroundImage.png';
import RouterUtils from 'services/utils/RouterUtils';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import { getRightSubtitle, getEntryPoint } from './GenreRailsLevel';
import Grid from '../../components/Grid';
import { useRouter } from '../../../hooks/useRouter';

interface UriParams {
  menuItemId: string;
  railId: string;
}

export default function GridLevel(): JSX.Element | null {
  const history = useHistoryWithState();
  const router = useRouter();
  const uriParams = useParams<UriParams>();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);

  const { menuItemId: menuItemIdString, railId } = uriParams;
  const menuItemId = useMemo(() => Number.parseInt(menuItemIdString, 10) || 0, [menuItemIdString]);
  const menuItem = useSelector((store: StoreState) =>
    Ducks.Menu.Selectors.getMenuItemByAreaAndId(store, MenuUiArea.MY_TV, menuItemId)
  );

  const rail = useSelector((store: StoreState) => MyTV.Selectors.getThirdLevelAssets(store));
  const totalAssets = useSelector((store: StoreState) => MyTV.Selectors.getThirdLevelAssetsNumber(store));
  const error = useSelector((store: StoreState) => MyTV.Selectors.getThirdLevelError(store));

  useEffect(() => {
    // TODO: An Error should not do a redirect without informing the user about the error
    // show an error modal when global modals are implemented
    if (error) {
      history.push(PrivateRoutePath.MYTV);
    }
  }, [error, history]);

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    dispatch(MyTV.ActionCreators.getThirdLevelAssets(railId, currentPage, menuItemId));
  }, [menuItemId, currentPage, dispatch, railId]);

  const existsMoreAssetsToLoad = Constants.ASSETS_PER_GRID_PAGE * currentPage - (totalAssets || 0) < 0;

  const backLabel = getRightSubtitle(menuItem?.type);
  const backUrl = menuItemId
    ? RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.MYTV_RAIL, [menuItemId])
    : PrivateRoutePath.MYTV;

  // #region INFINIT SCROLL
  const gridRef = useRef(null);
  const isOnViewport = useIsOnViewportObserver(gridRef, {
    root: null,
    rootMargin: '0px',
    threshold: [0.9, 1],
  });
  useEffect(() => {
    if (isOnViewport && existsMoreAssetsToLoad) {
      dispatch(MyTV.ActionCreators.getThirdLevelAssets(railId, currentPage + 1, menuItemId));
      setCurrentPage(currentPage + 1);
    }
  }, [menuItemId, currentPage, dispatch, existsMoreAssetsToLoad, isOnViewport, railId]);
  // #endregion INFINIT SCROLL

  if (!rail) {
    return (
      <div className="layout-spinner-container">
        <Spinner isActive />
      </div>
    );
  }

  const onCardClick = (asset: Asset, menuItemId_?: number) => {
    router.push({ asset, entryPoint: getEntryPoint(menuItem?.type), menuItemId: menuItemId_ });
  };

  return (
    <div className="ondemand-nextlevel ondemand-grid template-holder back-nav" id="MyTv_grid">
      <Overlay backgroundImage={BackgroundOverlay} />
      {menuItemId ? (
        <SimpleHeader subtitle={rail.railName || ''} backLabel={backLabel} backUrl={backUrl} />
      ) : (
        <SimpleHeader subtitle={rail.railName || ''} hideBackIcon />
      )}

      <Grid
        railId="grid"
        menuItemId={rail.menuItemId}
        className="no-background"
        assets={rail.assets}
        onCardClick={onCardClick}
      />
      <div className="load-more-btn-container margin-top-20 margin-bottom-66" ref={gridRef}>
        {!rail ? (
          <div className="layout-spinner-container">
            <Spinner isActive />
          </div>
        ) : null}
      </div>
    </div>
  );
}
